import { useState, useEffect } from 'react';
import { paymentsAPI, bookingsAPI } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { 
  CreditCard, User, Search, Filter, CheckCircle, AlertCircle, 
  PoundSterling, Calendar, RefreshCw, ChevronDown, Mail, Clock, X, Play, Pencil, Trash2, GraduationCap
} from 'lucide-react';
import { formatPrice, formatDate } from '../../lib/utils';
import CampBookingsPanel from './CampBookingsPanel';

const PendingPaymentsTab = () => {
  const [loading, setLoading] = useState(true);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  
  // Edit amount state
  const [editingAmountId, setEditingAmountId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  
  // Scheduling state
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledPayments, setScheduledPayments] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Top-up state
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpStudent, setTopUpStudent] = useState('');
  const [topUpEmail, setTopUpEmail] = useState('');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNote, setTopUpNote] = useState('');
  const [topUpProcessing, setTopUpProcessing] = useState(false);

  // Scholarship state
  const [showScholarship, setShowScholarship] = useState(false);
  const [scholarStudent, setScholarStudent] = useState('');
  const [scholarClassIds, setScholarClassIds] = useState([]);
  const [scholarNote, setScholarNote] = useState('Scholarship');
  const [scholarProcessing, setScholarProcessing] = useState(false);
  const [allClasses, setAllClasses] = useState([]);

  // "Mark as already paid + edit amount" modal state
  const [markPaidPayment, setMarkPaidPayment] = useState(null);
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidNote, setMarkPaidNote] = useState('');
  const [markPaidTermEnd, setMarkPaidTermEnd] = useState('');
  const [markPaidProcessing, setMarkPaidProcessing] = useState(false);

  useEffect(() => {
    fetchPendingPayments();
    fetchScheduledPayments();
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      setAllClasses(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      const res = await paymentsAPI.getPendingPayments();
      setPendingPayments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch pending payments:', error);
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledPayments = async () => {
    setLoadingScheduled(true);
    try {
      const res = await paymentsAPI.getScheduledPayments();
      setScheduledPayments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch scheduled payments:', error);
    } finally {
      setLoadingScheduled(false);
    }
  };

  const handleSyncAll = async () => {
    if (!confirm('Recalculate bookings for ALL students? This will apply correct bundle and sibling discounts.')) return;
    setSyncing(true);
    try {
      const res = await api.post('/students/sync-all-bookings');
      const data = res.data;
      toast.success(data.message || 'All bookings recalculated');
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate');
    } finally {
      setSyncing(false);
    }
  };

  const handleDedup = async () => {
    if (!confirm('Remove duplicate bookings? This keeps the most recent booking for each student+class and removes older duplicates.')) return;
    setSyncing(true);
    try {
      const res = await api.post('/students/dedup-bookings');
      toast.success(res.data.message || 'Duplicates removed');
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove duplicates');
    } finally {
      setSyncing(false);
    }
  };


  const handleSelectAll = (checked) => {
    if (checked) {
      const eligibleIds = filteredPayments
        .filter(p => p.has_saved_card)
        .map(p => p.id);
      setSelectedIds(new Set(eligibleIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleEditAmount = (payment) => {
    setEditingAmountId(payment.id);
    setEditAmount(payment.amount?.toString() || '0');
  };

  const handleSaveAmount = async (payment) => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      // Get all actual booking IDs for this grouped payment
      const bookingIds = payment.booking_ids || [payment.id];
      
      // Calculate amount per booking (evenly distributed)
      const amountPerBooking = amount / bookingIds.length;
      
      // Update each individual booking
      const updatePromises = bookingIds.map(id => 
        bookingsAPI.updateAmount(id, amountPerBooking)
      );
      await Promise.all(updatePromises);
      
      toast.success(`Amount updated to £${amount.toFixed(2)}`);
      setEditingAmountId(null);
      fetchPendingPayments(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update amount');
    }
  };

  const handleMarkAsPaid = (payment) => {
    // Open the proper modal so admin can also correct the amount + add a note.
    setMarkPaidPayment(payment);
    setMarkPaidAmount(payment.amount?.toString() || '0');
    setMarkPaidNote('');
    // Default term-end to existing value or empty (admin sets explicitly for pro-rated joins)
    setMarkPaidTermEnd(
      payment.term_end_date ? payment.term_end_date.split('T')[0] : ''
    );
  };

  const submitMarkPaid = async () => {
    if (!markPaidPayment) return;
    const amount = parseFloat(markPaidAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Enter a valid amount (£0 or more)');
      return;
    }
    setMarkPaidProcessing(true);
    try {
      const bookingIds = markPaidPayment.booking_ids || [markPaidPayment.id];
      const perBooking = bookingIds.length > 0 ? amount / bookingIds.length : amount;
      const note = markPaidNote.trim() || 'Marked paid via Take Payments tab';
      await Promise.all(
        bookingIds.map((id) =>
          api.patch(`/bookings/admin/${id}/mark-paid`, {
            amount: Number(perBooking.toFixed(2)),
            note,
          })
        )
      );
      // If admin explicitly set a term-end date (e.g. pro-rated mid-term join),
      // align ALL the booking ids in this group so they renew together.
      if (markPaidTermEnd) {
        await Promise.all(
          bookingIds.map((id) =>
            api.patch(`/bookings/${id}/term-end-date?term_end_date=${markPaidTermEnd}`)
          )
        );
      }
      toast.success(
        `${markPaidPayment.student_name} marked as paid (£${amount.toFixed(2)})${
          markPaidTermEnd ? ` · renews ${markPaidTermEnd}` : ''
        }.`
      );
      setMarkPaidPayment(null);
      setMarkPaidAmount('');
      setMarkPaidNote('');
      setMarkPaidTermEnd('');
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setMarkPaidProcessing(false);
    }
  };

  const openTopUp = (payment) => {
    setTopUpStudent(payment?.student_name || '');
    setTopUpEmail(payment?.parent_email || payment?.guardian_email || '');
    setTopUpAmount('');
    setTopUpNote('');
    setShowTopUp(true);
  };

  const handleTopUpPayment = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!topUpEmail) {
      toast.error('Parent email is required');
      return;
    }
    if (!confirm(`Charge £${amount.toFixed(2)} to ${topUpEmail}'s saved card for ${topUpStudent}?\n\nThis will charge their card immediately.`)) {
      return;
    }

    setTopUpProcessing(true);
    try {
      const res = await api.post('/payments/top-up-payment', {
        student_name: topUpStudent,
        parent_email: topUpEmail,
        amount: amount,
        note: topUpNote || `Top-up payment for ${topUpStudent}`,
      });
      if (res.data.success) {
        toast.success(`£${amount.toFixed(2)} charged successfully for ${topUpStudent}`);
        setShowTopUp(false);
        fetchPendingPayments();
      } else {
        toast.error(res.data.error || 'Payment failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top-up payment failed');
    } finally {
      setTopUpProcessing(false);
    }
  };

  const handleAddScholarship = async () => {
    if (!scholarStudent || scholarClassIds.length === 0) {
      toast.error('Select a student and at least one class');
      return;
    }
    setScholarProcessing(true);
    try {
      const res = await api.post('/payments/add-scholarship-booking', {
        student_name: scholarStudent,
        class_ids: scholarClassIds,
        note: scholarNote || 'Scholarship',
      });
      const successful = res.data.results?.filter(r => r.success) || [];
      const failed = res.data.results?.filter(r => !r.success) || [];
      if (successful.length > 0) {
        toast.success(`${scholarStudent} added to ${successful.length} class(es) as scholarship`);
      }
      if (failed.length > 0) {
        failed.forEach(f => toast.error(`${f.class_name}: ${f.error}`));
      }
      setShowScholarship(false);
      setScholarStudent('');
      setScholarClassIds([]);
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add scholarship');
    } finally {
      setScholarProcessing(false);
    }
  };



  const handleTakePayment = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one booking');
      return;
    }

    const selectedBookings = filteredPayments.filter(p => selectedIds.has(p.id));
    const totalAmount = selectedBookings.reduce((sum, p) => sum + (p.amount || 0), 0);

    if (!confirm(`Are you sure you want to charge ${selectedIds.size} booking(s) for a total of £${totalAmount.toFixed(2)}?`)) {
      return;
    }

    setProcessing(true);
    try {
      // Collect all actual booking IDs from selected grouped payments
      const allBookingIds = [];
      for (const payment of selectedBookings) {
        const ids = payment.booking_ids || [payment.id];
        allBookingIds.push(...ids);
      }
      
      const res = await paymentsAPI.takePayment(allBookingIds);
      setLastResult(res.data);
      
      if (res.data.successful > 0) {
        toast.success(`Successfully charged ${res.data.successful} booking(s) - £${res.data.total_amount_charged.toFixed(2)}`);
      }
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} payment(s) failed`);
      }
      
      // Refresh the list
      await fetchPendingPayments();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payments');
    } finally {
      setProcessing(false);
    }
  };

  const handleSchedulePayment = async () => {
    if (!scheduledDate) {
      toast.error('Please select a date');
      return;
    }

    const bookingIds = selectedIds.size > 0 ? Array.from(selectedIds) : [];
    const count = bookingIds.length || eligibleCount;
    
    if (!confirm(`Schedule ${count} payment(s) to be automatically charged on ${new Date(scheduledDate).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}?`)) {
      return;
    }

    setScheduling(true);
    try {
      const res = await paymentsAPI.scheduleBulkPayment({
        scheduled_date: scheduledDate,
        booking_ids: bookingIds
      });
      
      toast.success(res.data.message);
      setShowScheduler(false);
      setScheduledDate('');
      setSelectedIds(new Set());
      
      // Refresh both lists
      await fetchPendingPayments();
      await fetchScheduledPayments();
    } catch (error) {
      console.error('Schedule error:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule payments');
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelScheduled = async (scheduleId) => {
    if (!confirm('Are you sure you want to cancel this scheduled payment batch?')) {
      return;
    }

    try {
      await paymentsAPI.cancelScheduledPayment(scheduleId);
      toast.success('Scheduled payment cancelled');
      await fetchScheduledPayments();
      await fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleProcessNow = async (scheduleId) => {
    if (!confirm('Process this scheduled payment batch immediately?')) {
      return;
    }

    setProcessing(true);
    try {
      const res = await paymentsAPI.processScheduledPaymentNow(scheduleId);
      
      if (res.data.successful > 0) {
        toast.success(`Successfully charged ${res.data.successful} booking(s)`);
      }
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} payment(s) failed`);
      }
      
      await fetchScheduledPayments();
      await fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessing(false);
    }
  };

  // Filter payments
  const filteredPayments = pendingPayments.filter(payment => {
    const matchesSearch = 
      payment.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.parent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.parent_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.class_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesClass = filterClass === 'all' || payment.class_name === filterClass;
    
    return matchesSearch && matchesClass;
  });

  // Get unique class names for filter
  const classNames = [...new Set(pendingPayments.map(p => p.class_name))].filter(Boolean);

  // Calculate totals
  const selectedTotal = filteredPayments
    .filter(p => selectedIds.has(p.id))
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const eligibleCount = filteredPayments.filter(p => p.has_saved_card).length;

  if (loading) {
    return (
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white/50">Loading pending payments...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Pending Payments
          </h3>
          <p className="text-white/60 text-sm mt-1">
            {pendingPayments.length} booking(s) awaiting payment authorization
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openTopUp(null)}
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            data-testid="top-up-btn-header"
          >
            <PoundSterling className="w-4 h-4 mr-2" />
            Top-up Payment
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDedup}
            disabled={syncing}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="dedup-bookings-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {syncing ? 'Processing...' : 'Remove Duplicates'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSyncAll}
            disabled={syncing}
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            data-testid="sync-all-bookings-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Recalculating...' : 'Recalculate All'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchPendingPayments}
            className="border-white/20 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Camp bookings awaiting payment (card captured at signup) */}
      <CampBookingsPanel />

      {/* Filters */}
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search by name, email, or class..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#1a1a1a] border-[#333] text-white"
                data-testid="pending-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-white/40" />
              <select
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
                className="bg-[#1a1a1a] border border-[#333] rounded-md px-3 py-2 text-white text-sm"
                data-testid="pending-class-filter"
              >
                <option value="all">All Classes</option>
                {classNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Bar */}
      {filteredPayments.length > 0 && (
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedIds.size === eligibleCount && eligibleCount > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-payments"
                  />
                  <span className="text-white/70 text-sm">
                    Select all with saved cards ({eligibleCount})
                  </span>
                </div>
                <Badge className="bg-white/10 text-white">
                  {selectedIds.size} selected
                </Badge>
                {selectedIds.size > 0 && (
                  <Badge className="bg-primary/20 text-primary">
                    Total: £{selectedTotal.toFixed(2)}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => openTopUp(null)}
                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  data-testid="top-up-btn"
                >
                  <PoundSterling className="w-4 h-4 mr-2" />
                  Top-up Payment
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowScholarship(true)}
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid="scholarship-btn"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Add Scholarship
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowScheduler(true)}
                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                  data-testid="schedule-payment-btn"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule for Date
                </Button>
                <Button
                  onClick={handleTakePayment}
                  disabled={selectedIds.size === 0 || processing}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="take-payment-btn"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <PoundSterling className="w-4 h-4 mr-2" />
                      Take Payment Now ({selectedIds.size})
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Payment Modal */}
      {/* Schedule Payment Modal */}
      {showScheduler && (
        <Card className="bg-[#1a1a1a] border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              Schedule Bulk Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-white/60 text-sm">
              {selectedIds.size > 0 
                ? `Schedule ${selectedIds.size} selected payment(s) to be automatically charged on a specific date.`
                : `Schedule all ${eligibleCount} eligible payment(s) to be automatically charged on a specific date.`
              }
            </p>
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-white/70 text-sm mb-2">Payment Date</label>
                <Input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-[#111] border-[#333] text-white"
                  data-testid="schedule-date-input"
                />
              </div>
              <Button
                onClick={handleSchedulePayment}
                disabled={!scheduledDate || scheduling}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="confirm-schedule-btn"
              >
                {scheduling ? 'Scheduling...' : 'Schedule Payment'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowScheduler(false);
                  setScheduledDate('');
                }}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top-up Payment Modal */}
      {showTopUp && (
        <Card className="bg-[#1a1a1a] border-amber-500/30" data-testid="top-up-modal">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <PoundSterling className="w-5 h-5 text-amber-400" />
              Top-up Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-white/60 text-sm">
              Charge an additional amount to a parent's saved card. Used for renewal shortfalls, manual adjustments, or additional fees.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">Student Name</label>
                <Input
                  value={topUpStudent}
                  onChange={(e) => setTopUpStudent(e.target.value)}
                  placeholder="e.g. Evelin Pican"
                  className="bg-[#111] border-[#333] text-white"
                  data-testid="topup-student-input"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Parent Email (must have saved card)</label>
                <Input
                  value={topUpEmail}
                  onChange={(e) => setTopUpEmail(e.target.value)}
                  placeholder="e.g. parent@email.com"
                  className="bg-[#111] border-[#333] text-white"
                  data-testid="topup-email-input"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Amount (£)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.30"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  placeholder="e.g. 145.16"
                  className="bg-[#111] border-[#333] text-white"
                  data-testid="topup-amount-input"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Note (optional)</label>
                <Input
                  value={topUpNote}
                  onChange={(e) => setTopUpNote(e.target.value)}
                  placeholder="e.g. Renewal shortfall top-up"
                  className="bg-[#111] border-[#333] text-white"
                  data-testid="topup-note-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={handleTopUpPayment}
                disabled={!topUpEmail || !topUpAmount || topUpProcessing}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-topup-btn"
              >
                {topUpProcessing ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Charging...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Charge £{topUpAmount ? parseFloat(topUpAmount).toFixed(2) : '0.00'}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowTopUp(false)}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Payments List */}

      {/* Add Scholarship Modal */}
      {showScholarship && (
        <Card className="bg-[#1a1a1a] border-emerald-500/30" data-testid="scholarship-modal">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-emerald-400" />
              Add Scholarship Student to Register
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-white/60 text-sm">
              Add a student to class registers with a £0 scholarship booking. They will appear on the register for selected classes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">Student Name</label>
                <Input
                  value={scholarStudent}
                  onChange={(e) => setScholarStudent(e.target.value)}
                  placeholder="e.g. Pepper Spinks"
                  className="bg-[#111] border-[#333] text-white"
                  data-testid="scholar-student-input"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Note</label>
                <Input
                  value={scholarNote}
                  onChange={(e) => setScholarNote(e.target.value)}
                  placeholder="e.g. Scholarship 2026"
                  className="bg-[#111] border-[#333] text-white"
                  data-testid="scholar-note-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Select Classes</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {allClasses.map(c => (
                  <label key={c.id} className="flex items-center gap-2 p-2 bg-[#111] border border-[#333] rounded cursor-pointer hover:border-emerald-500/50">
                    <input
                      type="checkbox"
                      checked={scholarClassIds.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setScholarClassIds(prev => [...prev, c.id]);
                        } else {
                          setScholarClassIds(prev => prev.filter(id => id !== c.id));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-white text-sm">{c.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={handleAddScholarship}
                disabled={!scholarStudent || scholarClassIds.length === 0 || scholarProcessing}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-scholarship-btn"
              >
                {scholarProcessing ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Add to {scholarClassIds.length} Class{scholarClassIds.length !== 1 ? 'es' : ''}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowScholarship(false); setScholarClassIds([]); }}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {scheduledPayments.length > 0 && (
        <Card className="bg-[#111] border-cyan-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Calendar className="w-4 h-4 text-cyan-400" />
              Scheduled Payments ({scheduledPayments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {scheduledPayments.map(scheduled => (
              <div 
                key={scheduled.id} 
                className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg border border-[#333]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {new Date(scheduled.scheduled_date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <p className="text-white/60 text-sm">
                      {scheduled.booking_count} booking(s) • £{scheduled.total_amount?.toFixed(2)}
                    </p>
                    <p className="text-white/40 text-xs">
                      Scheduled by {scheduled.created_by_name} on {new Date(scheduled.created_at).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={
                    scheduled.status === 'scheduled' ? 'bg-cyan-500/20 text-cyan-400' :
                    scheduled.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-green-500/20 text-green-400'
                  }>
                    {scheduled.status}
                  </Badge>
                  {scheduled.status === 'scheduled' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleProcessNow(scheduled.id)}
                        disabled={processing}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        title="Process now"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelScheduled(scheduled.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Last Result */}
      {lastResult && (
        <Card className={`border ${lastResult.failed > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {lastResult.failed > 0 ? (
                <AlertCircle className="w-5 h-5 text-yellow-400" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-400" />
              )}
              <div className="flex-1">
                <p className={lastResult.failed > 0 ? 'text-yellow-400' : 'text-green-400'}>
                  Payment batch completed: {lastResult.successful} successful, {lastResult.failed} failed
                </p>
                <p className="text-white/60 text-sm">
                  Total charged: £{lastResult.total_amount_charged.toFixed(2)}
                </p>
                {lastResult.results && lastResult.results.filter(r => !r.success && r.error).length > 0 && (
                  <div className="mt-2 text-xs text-red-400">
                    <p className="font-medium">Errors:</p>
                    {lastResult.results.filter(r => !r.success && r.error).slice(0, 5).map((r, i) => (
                      <div key={i} className="mt-1">
                        <p>• {r.error}</p>
                        {r.needs_new_card && r.user_id && (
                          <div className="mt-1 ml-3 flex flex-wrap items-center gap-1.5">
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await api.post('/payments/admin/fix-stale-customer', { user_id: r.user_id });
                                  const d = res.data;
                                  if (d.action === 'merged') {
                                    toast.success(`Moved ${d.moved_count} card${d.moved_count === 1 ? '' : 's'} to the correct Stripe customer. Retry the batch now.`);
                                  } else if (d.action === 'cleared_dead_field') {
                                    toast.success('Cleared a stale reference — try the batch again.');
                                  } else {
                                    toast.info('No stale customer to merge for this parent.');
                                  }
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || 'Failed to fix card link');
                                }
                              }}
                              className="inline-flex items-center gap-1 text-[11px] bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 rounded-md px-2 py-0.5"
                              data-testid={`fix-stale-customer-${r.booking_id || r.id || i}`}
                            >
                              🔧 Fix card link (parent already added)
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await api.post('/payments/admin/send-update-card-email', { user_id: r.user_id });
                                  toast.success(`Email sent to ${res.data.sent_to} — they can save a new card and you can retry.`);
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || 'Failed to send email');
                                }
                              }}
                              className="inline-flex items-center gap-1 text-[11px] bg-pink-500/20 hover:bg-pink-500/30 text-pink-200 border border-pink-500/40 rounded-md px-2 py-0.5"
                              data-testid={`send-update-card-email-${r.booking_id || r.id || i}`}
                            >
                              ✉ Email {r.parent_name || 'parent'} to update card
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setLastResult(null)}
                className="ml-auto text-white/50"
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments List */}
      {filteredPayments.length === 0 ? (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-8 text-center">
            <CreditCard className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2 text-white">No Pending Payments</h3>
            <p className="text-white/50">
              {searchTerm || filterClass !== 'all' 
                ? "No payments match your search criteria"
                : "All payments have been processed or no bookings are awaiting authorization"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPayments.map(payment => (
            <Card 
              key={payment.id}
              className={`bg-[#111] border-[#333] transition-all ${
                selectedIds.has(payment.id) ? 'ring-2 ring-primary border-primary' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Checkbox */}
                  <Checkbox
                    checked={selectedIds.has(payment.id)}
                    onCheckedChange={(checked) => handleSelectOne(payment.id, checked)}
                    disabled={!payment.has_saved_card}
                    data-testid={`select-payment-${payment.id}`}
                  />
                  
                  {/* Main Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-white">{payment.student_name}</span>
                      <span className="text-white/40">•</span>
                      <span className="text-white/60">{payment.class_name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-white/50">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {payment.parent_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {payment.parent_email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(payment.created_at).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                  </div>

                  {/* Card Status */}
                  <div className="flex items-center gap-3">
                    {payment.has_saved_card ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CreditCard className="w-3 h-3 mr-1" />
                        •••• {payment.card_last4}
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        No Card
                      </Badge>
                    )}
                  </div>

                  {/* Amount */}
                  <div className="text-right min-w-[100px]">
                    {editingAmountId === payment.id ? (
                      <div className="flex items-center gap-1">
                        <span className="text-white/60">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          className="w-20 h-8 text-right bg-[#1a1a1a] border-[#444] text-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveAmount(payment);
                            if (e.key === 'Escape') setEditingAmountId(null);
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSaveAmount(payment)}
                          className="h-8 px-2 text-green-400 hover:bg-green-500/10"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingAmountId(null)}
                          className="h-8 px-2 text-white/50 hover:bg-white/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div>
                          <span className="text-xl font-bold text-primary">
                            {formatPrice(payment.amount)}
                          </span>
                          <p className="text-xs text-white/40">{payment.pricing_type || 'term'}</p>
                        </div>
                        <button
                          onClick={() => handleEditAmount(payment)}
                          className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                          title="Edit amount"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleMarkAsPaid(payment)}
                          className="p-1.5 rounded hover:bg-green-500/20 text-green-400/60 hover:text-green-400 transition-colors"
                          title="Mark as Paid (manual payment received)"
                          data-testid={`mark-paid-${payment.id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openTopUp(payment)}
                          className="p-1.5 rounded hover:bg-amber-500/20 text-amber-400/60 hover:text-amber-400 transition-colors"
                          title="Top-up payment (charge additional amount)"
                          data-testid={`topup-${payment.id}`}
                        >
                          <PoundSterling className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`Delete ${payment.student_name}'s pending payment (£${payment.amount?.toFixed(2)})?\n\nThis removes the duplicate entry. This cannot be undone.`)) return;
                            try {
                              await api.post('/payments/delete-pending-payment', {
                                booking_ids: payment.booking_ids || [payment.id],
                                source: payment.source,
                                student_name: payment.student_name,
                                user_id: payment.user_id
                              });
                              toast.success(`Deleted ${payment.student_name}'s entry`);
                              fetchPendingPayments();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to delete');
                            }
                          }}
                          className="p-1.5 rounded hover:bg-red-500/20 text-red-400/60 hover:text-red-400 transition-colors"
                          title="Delete this entry (remove duplicate)"
                          data-testid={`delete-payment-${payment.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Summary Footer */}
      {filteredPayments.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm">
              <div className="flex gap-6">
                <span className="text-white/50">
                  Total Bookings: <span className="text-white font-medium">{filteredPayments.length}</span>
                </span>
                <span className="text-white/50">
                  With Saved Cards: <span className="text-green-400 font-medium">{eligibleCount}</span>
                </span>
                <span className="text-white/50">
                  Missing Cards: <span className="text-red-400 font-medium">{filteredPayments.length - eligibleCount}</span>
                </span>
              </div>
              <span className="text-white/50">
                Total Value: <span className="text-primary font-bold">
                  £{filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                </span>
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mark As Already Paid + edit amount modal */}
      {markPaidPayment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={(e) => { if (e.target === e.currentTarget) setMarkPaidPayment(null); }}
          data-testid="mark-paid-modal"
        >
          <Card className="w-full max-w-md bg-[#111] border-emerald-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                Mark as already paid
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-white/50">Student:</span> <span className="text-white">{markPaidPayment.student_name}</span></p>
                {markPaidPayment.class_name && (
                  <p><span className="text-white/50">Class:</span> <span className="text-white">{markPaidPayment.class_name}</span></p>
                )}
                <p><span className="text-white/50">Original amount:</span> <span className="text-white">£{(markPaidPayment.amount || 0).toFixed(2)}</span></p>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-1">Actual amount paid (£)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={markPaidAmount}
                  onChange={(e) => setMarkPaidAmount(e.target.value)}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="mark-paid-amount-input"
                />
                <p className="text-xs text-white/40 mt-1">
                  Edit if you charged a pro-rated / corrected amount in Stripe (e.g. £36 instead of £54).
                </p>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-1">Note (audit trail)</label>
                <Input
                  value={markPaidNote}
                  onChange={(e) => setMarkPaidNote(e.target.value)}
                  placeholder="e.g. paid via Stripe, pro-rated to £36 for late join"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="mark-paid-note-input"
                />
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-1">Term ends on (optional)</label>
                <Input
                  type="date"
                  value={markPaidTermEnd}
                  onChange={(e) => setMarkPaidTermEnd(e.target.value)}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="mark-paid-term-end-input"
                />
                <p className="text-xs text-pink-400 mt-1">
                  💡 For pro-rated mid-term joins: set this to the rest of the cohort's term end date so the
                  student appears in <strong>Renewals</strong> at the right time. Leave blank to keep the
                  default (~6 weeks from today).
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setMarkPaidPayment(null)}
                  className="border-[#333] text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitMarkPaid}
                  disabled={markPaidProcessing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="mark-paid-submit"
                >
                  {markPaidProcessing ? 'Saving…' : 'Mark as paid'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PendingPaymentsTab;
